import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useHaptics } from "@/hooks/useHaptics";

export interface SavedRestaurant {
  id: string;
  airport_code: string;
  restaurant_id: string;
  restaurant_name: string;
  category: string | null;
  terminal: string | null;
  url: string | null;
  created_at: string;
}

export interface SaveRestaurantInput {
  airportCode: string;
  restaurantId: string | number;
  restaurantName: string;
  category?: string | null;
  terminal?: string | null;
  url?: string | null;
}

export const useSavedRestaurants = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { notification, impact } = useHaptics();
  const [savedRestaurants, setSavedRestaurants] = useState<SavedRestaurant[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSaved = useCallback(async () => {
    if (!user) {
      setSavedRestaurants([]);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from("saved_restaurants")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (!error && data) setSavedRestaurants(data as SavedRestaurant[]);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchSaved();
  }, [fetchSaved]);

  const isSaved = useCallback(
    (airportCode: string, restaurantId: string | number) =>
      savedRestaurants.some(
        (r) => r.airport_code === airportCode && r.restaurant_id === String(restaurantId)
      ),
    [savedRestaurants]
  );

  const toggleSave = useCallback(
    async (input: SaveRestaurantInput) => {
      if (!user) {
        toast({
          title: "Sign in to save restaurants",
          description: "Create a free account to keep your favorite spots.",
        });
        return false;
      }

      const restaurantId = String(input.restaurantId);

      if (isSaved(input.airportCode, restaurantId)) {
        const { error } = await supabase
          .from("saved_restaurants")
          .delete()
          .eq("user_id", user.id)
          .eq("airport_code", input.airportCode)
          .eq("restaurant_id", restaurantId);

        if (!error) {
          setSavedRestaurants((prev) =>
            prev.filter(
              (r) => !(r.airport_code === input.airportCode && r.restaurant_id === restaurantId)
            )
          );
          impact("light");
          toast({ title: "Removed from saved" });
          return true;
        }
        return false;
      }

      const { data, error } = await supabase
        .from("saved_restaurants")
        .insert({
          user_id: user.id,
          airport_code: input.airportCode,
          restaurant_id: restaurantId,
          restaurant_name: input.restaurantName,
          category: input.category ?? null,
          terminal: input.terminal ?? null,
          url: input.url ?? null,
        })
        .select()
        .single();

      if (!error && data) {
        setSavedRestaurants((prev) => [data as SavedRestaurant, ...prev]);
        notification("success");
        toast({
          title: "Restaurant saved!",
          description: `${input.restaurantName} added to your favorites.`,
        });
        return true;
      }
      return false;
    },
    [user, isSaved, toast, impact, notification]
  );

  return { savedRestaurants, loading, isSaved, toggleSave, refetch: fetchSaved };
};
